import { useMemo, useRef } from "react";
import Supercluster from "supercluster";

/**
 * Sort comparator: unseen notes first, then most recent first.
 */
function unseenRecentFirst(a, b) {
  const aViewed = a?.isOwner || a?.isViewedByCurrentUser;
  const bViewed = b?.isOwner || b?.isViewedByCurrentUser;
  if (aViewed !== bViewed) return aViewed ? 1 : -1;
  const dateA = a?.createdAt ? new Date(a.createdAt) : 0;
  const dateB = b?.createdAt ? new Date(b.createdAt) : 0;
  return dateB - dateA;
}

/**
 * useClusters - Groups nearby notes into clusters based on map zoom level.
 *
 * At zoom >= 17 (beyond Supercluster's maxZoom of 16), notes that share
 * nearly identical coordinates are grouped into "colocated" items so they
 * can be expanded inline rather than stacking on top of each other.
 *
 * @param {Array}  notes   - Array of note objects with latitude/longitude
 * @param {Object} mapRef     - React ref to the react-map-gl Map component
 * @param {Object} viewState  - Current map viewState ({ longitude, latitude, zoom })
 * @param {string|null} selectedNoteId - ID of currently selected note (stays in clusters for stability)
 * @returns {{ clusters: Array, superclusterRef: React.RefObject }}
 */
export default function useClusters(
  notes,
  mapRef,
  viewState,
  selectedNoteId = null,
) {
  const { zoom = 10, longitude: vsLng, latitude: vsLat } = viewState || {};
  const superclusterRef = useRef(null);

  // Build GeoJSON features and Supercluster index when notes change.
  // All notes (including selected) stay in the index so clusters remain stable.
  const index = useMemo(() => {
    const sc = new Supercluster({
      radius: 35,
      maxZoom: 16,
    });

    const points = notes
      .filter(
        (n) =>
          n.latitude &&
          n.longitude &&
          !isNaN(n.latitude) &&
          !isNaN(n.longitude),
      )
      .map((note) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [note.longitude, note.latitude],
        },
        properties: { noteData: note },
      }));

    sc.load(points);
    superclusterRef.current = sc;
    return sc;
  }, [notes]);

  // Compute clusters for current viewport
  const clusters = useMemo(() => {
    const map = mapRef?.current?.getMap();
    if (!map || !index) return [];

    // Use world extent instead of map.getBounds(). In react-map-gl
    // controlled mode, map.getBounds() reads from map.transform which
    // can be temporarily out of sync with the rendered tiles (which use
    // _renderTransform). Using fixed world bounds + viewState.zoom
    // (both from React state) keeps cluster computation consistent.
    const bounds = [-180, -85, 180, 85];
    const rawClusters = index.getClusters(bounds, Math.floor(zoom));

    const items = rawClusters.map((feature) => {
      if (feature.properties.cluster) {
        // Check if ALL notes in cluster have been viewed
        const allLeaves = index.getLeaves(feature.id, Infinity);
        const allViewed = allLeaves.every((leaf) => {
          const n = leaf.properties.noteData;
          return n?.isOwner || n?.isViewedByCurrentUser;
        });

        // Extract avatar previews: unseen notes first, then by most recent
        const previewLeaves = index.getLeaves(feature.id, 3);
        const avatars = previewLeaves
          .map((leaf) => ({
            avatar: leaf.properties.noteData?.owner?.avatar,
            name: leaf.properties.noteData?.owner?.name,
            createdAt: leaf.properties.noteData?.createdAt,
            isViewed:
              leaf.properties.noteData?.isOwner ||
              leaf.properties.noteData?.isViewedByCurrentUser,
          }))
          .sort(unseenRecentFirst);

        return {
          type: "cluster",
          id: feature.id,
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          count: feature.properties.point_count,
          avatars,
          allViewed,
        };
      }
      return {
        type: "note",
        note: feature.properties.noteData,
      };
    });

    // --- Co-location pass (zoom >= 17, beyond Supercluster's maxZoom) ---
    // Query Supercluster at zoom 16 (its maxZoom) to find which notes were
    // grouped together. Use those cluster assignments to form co-located groups,
    // so notes from separate zoom-16 clusters never merge.
    if (zoom >= 17) {
      const zoom16Clusters = index.getClusters(bounds, 16);

      // Build a map from note coordinates to their zoom-16 cluster ID
      const noteToCluster = new Map();
      const clusterMeta = new Map(); // cluster ID → { lng, lat }

      for (const feature of zoom16Clusters) {
        if (feature.properties.cluster) {
          const cId = feature.id;
          const leaves = index.getLeaves(cId, Infinity);
          clusterMeta.set(cId, {
            longitude: feature.geometry.coordinates[0],
            latitude: feature.geometry.coordinates[1],
          });
          for (const leaf of leaves) {
            const n = leaf.properties.noteData;
            if (n) noteToCluster.set(n.id, cId);
          }
        }
        // Individual notes at zoom 16 don't need co-location grouping
      }

      // Group current items by their zoom-16 cluster
      const groups = new Map(); // cluster ID → [note, ...]
      const ungrouped = [];

      for (const item of items) {
        if (item.type !== "note") {
          ungrouped.push(item);
          continue;
        }
        const cId = noteToCluster.get(item.note.id);
        if (cId != null) {
          if (!groups.has(cId)) groups.set(cId, []);
          groups.get(cId).push(item.note);
        } else {
          ungrouped.push(item);
        }
      }

      const result = [...ungrouped];

      for (const [cId, groupNotes] of groups) {
        if (groupNotes.length >= 2) {
          const sortedNotes = [...groupNotes].sort(unseenRecentFirst);

          const avatars = sortedNotes.map((n) => ({
            avatar: n?.owner?.avatar,
            name: n?.owner?.name,
            createdAt: n?.createdAt,
            isViewed: n?.isOwner || n?.isViewedByCurrentUser,
          }));

          const allViewed = sortedNotes.every(
            (n) => n?.isOwner || n?.isViewedByCurrentUser,
          );

          // Use the zoom-16 cluster center as the marker position
          const meta = clusterMeta.get(cId);
          const lng = meta?.longitude ??
            groupNotes.reduce((s, n) => s + n.longitude, 0) / groupNotes.length;
          const lat = meta?.latitude ??
            groupNotes.reduce((s, n) => s + n.latitude, 0) / groupNotes.length;

          result.push({
            type: "colocated",
            id: `coloc-${cId}`,
            longitude: lng,
            latitude: lat,
            count: sortedNotes.length,
            notes: sortedNotes,
            avatars,
            allViewed,
          });
        } else {
          // Single note from a zoom-16 cluster — render as individual
          result.push({ type: "note", note: groupNotes[0] });
        }
      }

      return result;
    }

    return items;
  }, [index, zoom, vsLng, vsLat, mapRef, selectedNoteId]);

  return { clusters, superclusterRef };
}
